import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, getErrorMessage } from "@/lib/api";

/**
 * Digio redirect return landing.
 * Digio sends the browser here when is_redirection_approach is enabled.
 * When from=app, open the CoinMBX app via deep link.
 */
export default function DigioKycCallback() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [message, setMessage] = useState("Finishing Digio KYC…");
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    const fromApp = params.get("from") === "app";

    const openAppDeepLink = (digioStatus, msg = "") => {
      const q = new URLSearchParams();
      q.set("digio", digioStatus || "done");
      if (msg) q.set("msg", msg);
      const deepLink = `coinmbx://kyc/digio-complete?${q.toString()}`;
      try {
        window.location.href = deepLink;
      } catch {
        /* ignore */
      }
      try {
        const iframe = document.createElement("iframe");
        iframe.style.display = "none";
        iframe.src = deepLink;
        document.body.appendChild(iframe);
        setTimeout(() => iframe.remove(), 2000);
      } catch {
        /* ignore */
      }
    };

    (async () => {
      try {
        const { data } = await api.post("/api/kyc/digio/sync", {});
        if (cancelled) return;
        const st = data?.digio?.status || (data?.autoApproved ? "approved" : "pending");
        if (fromApp) {
          setMessage("Opening CoinMBX app…");
          openAppDeepLink(st);
          setTimeout(() => {
            if (!cancelled) navigate("/kyc", { replace: true });
          }, 2500);
          return;
        }
        setMessage(data?.autoApproved ? "KYC approved. Redirecting…" : "Status updated. Redirecting…");
        setTimeout(() => {
          if (!cancelled) navigate("/kyc", { replace: true });
        }, 900);
      } catch (e) {
        if (cancelled) return;
        const msg = getErrorMessage(e);
        setErr(msg);
        setMessage("Could not sync Digio status.");
        if (fromApp) openAppDeepLink("error", msg);
        setTimeout(() => {
          if (!cancelled) navigate("/kyc", { replace: true });
        }, 2200);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate, params]);

  return (
    <div className="min-h-[60vh] grid place-items-center px-6">
      <div className="text-center max-w-md space-y-3">
        <Loader2 className="animate-spin mx-auto text-cm-blue" size={28} />
        <p className="text-[15px] font-medium text-cm-navy">{message}</p>
        {err ? <p className="text-[13px] text-red-600">{err}</p> : null}
      </div>
    </div>
  );
}
